// Glue between stored documents and the pure engine. Used by the browser and by Convex (period close).
import type { Breakdown, Chain, Codes, Deliverable, Frozen, LoeSpan, Period, Settings } from "../engine/types";
import type { ProjectData } from "../engine/compute";
import { BREAKDOWNS, LOE_CHAIN } from "../engine/types";
import { loeSteps } from "../engine/loe";

export interface StoredStep {
  milestone: string;
  planned?: string | null;
  forecast?: string | null;
  earned?: string | null;
  codes?: Codes;
  planningRef?: string;
}
export interface StoredDeliverable {
  _id: string;
  code: string;
  title?: string;
  weight: number;
  unit?: "h" | "$";
  codes: Codes;
  chainCode: string;
  steps: StoredStep[];
  loe?: LoeSpan;
  frozen?: Frozen["deliverables"][string];
  deleted?: boolean;
}
export interface StoredPeriod {
  number: number;
  start: string;
  cutoff: string;
  stdHours: number;
  monthEnd: boolean;
  closed: boolean;
}
export interface StoredChangeOrder {
  _id: string;
  kind: "budget" | "weight";
  status: "pending" | "approved" | "rejected";
  periodNumber: number;
  number: string;
  deliverableId?: string;
  newWeight?: number;
}
export interface StoredBundle {
  periods: StoredPeriod[];
  chains: { code: string; steps: { milestone: string; cum: number; mfgCum?: number }[] }[];
  deliverables: StoredDeliverable[];
  changeOrders: StoredChangeOrder[];
  actuals: { periodNumber: number; hours: number; codes: Codes; indirect: boolean }[];
  indirects: { _id: string; title: string; budget: number; start: string; end: string; codes: Codes }[];
  settings: Settings;
}

export function sortedPeriods(ps: StoredPeriod[]): Period[] {
  return [...ps].sort((a, b) => a.number - b.number).map(({ number, start, cutoff, stdHours, monthEnd }) => ({ number, start, cutoff, stdHours, monthEnd }));
}

/** Index of the current (first open) period. All closed: the last one. No calendar: -1. */
export function currentPeriodIdx(ps: StoredPeriod[]): number {
  const s = [...ps].sort((a, b) => a.number - b.number);
  if (!s.length) return -1;
  const i = s.findIndex((p) => !p.closed);
  return i < 0 ? s.length - 1 : i;
}

export function lastClosedIdx(ps: StoredPeriod[]): number {
  const s = [...ps].sort((a, b) => a.number - b.number);
  let idx = -1;
  s.forEach((p, i) => p.closed && (idx = i));
  return idx;
}

export function cleanCodes(c: Codes | undefined): Codes {
  const out: Codes = {};
  if (!c) return out;
  for (const b of BREAKDOWNS) {
    const v = c[b as Breakdown];
    if (v) out[b as Breakdown] = v;
  }
  return out;
}

/**
 * Resolve a stored deliverable into an engine deliverable (cumulative % from its chain, approved weight COs).
 * An LOE deliverable gets one step per period of its span; that needs the calendar (without it, no steps).
 */
export function toEngineDeliverable(d: StoredDeliverable, chains: Map<string, Chain>, cos: StoredChangeOrder[], cal?: { periods: Period[]; currentIdx: number }): Deliverable | null {
  const weightChanges = cos
    .filter((c) => c.kind === "weight" && c.status === "approved" && c.deliverableId === d._id && c.newWeight !== undefined)
    .map((c) => ({ periodNumber: c.periodNumber, weight: c.newWeight!, ref: c.number }));
  if (d.loe)
    return {
      id: d._id,
      code: d.code,
      title: d.title,
      weight: d.weight,
      unit: d.unit ?? "h",
      codes: cleanCodes(d.codes),
      chainCode: LOE_CHAIN,
      weightChanges,
      steps: cal ? loeSteps(d.loe, cal.periods, cal.currentIdx) : [],
    };
  const chain = chains.get(d.chainCode);
  if (!chain) return null;
  return {
    id: d._id,
    code: d.code,
    title: d.title,
    weight: d.weight,
    unit: d.unit ?? "h",
    codes: cleanCodes(d.codes),
    chainCode: d.chainCode,
    weightChanges,
    steps: chain.steps.map((cs, i) => {
      const s = d.steps[i];
      return {
        milestone: cs.milestone,
        cum: cs.cum,
        planned: s?.planned ?? null,
        forecast: s?.forecast ?? null,
        earned: s?.earned ?? null,
        codes: cleanCodes(s?.codes),
        planningRef: s?.planningRef,
      };
    }),
  };
}

const EMPTY_FROZEN: Frozen["deliverables"][string] = {
  bookings: { planned: [], earned: [], forecast: [] },
  reached: { planned: 0, earned: 0, forecast: 0 },
};

export function toProjectData(b: StoredBundle): { data: ProjectData; chains: Map<string, Chain>; unresolved: string[] } {
  const chains = new Map<string, Chain>(b.chains.map((c) => [c.code, { code: c.code, steps: c.steps }]));
  const periods = sortedPeriods(b.periods);
  const unresolved: string[] = [];
  const deliverables: Deliverable[] = [];
  const frozenDeliverables: Frozen["deliverables"] = {};
  const cal = { periods, currentIdx: Math.max(0, currentPeriodIdx(b.periods)) };
  for (const d of b.deliverables) {
    if (d.deleted) continue;
    const ed = toEngineDeliverable(d, chains, b.changeOrders, cal);
    if (!ed) {
      unresolved.push(d.code);
      continue;
    }
    deliverables.push(ed);
    // A deliverable added after a close has no frozen state: it starts empty, so nothing lands in closed periods (rule 16).
    frozenDeliverables[d._id] = d.frozen ?? EMPTY_FROZEN;
  }
  const lc = lastClosedIdx(b.periods);
  const data: ProjectData = {
    periods,
    deliverables,
    actuals: b.actuals.map((a) => ({ periodNumber: a.periodNumber, hours: a.hours, codes: cleanCodes(a.codes), indirect: a.indirect })),
    indirects: b.indirects.map((i) => ({ id: i._id, title: i.title, budget: i.budget, start: i.start, end: i.end, codes: cleanCodes(i.codes) })),
    settings: b.settings,
    currentIdx: Math.max(0, currentPeriodIdx(b.periods)),
    frozen: lc >= 0 ? { lastClosedIdx: lc, deliverables: frozenDeliverables } : undefined,
  };
  return { data, chains, unresolved };
}

/**
 * A breakdown's nodes in tree order: each parent followed by its children, siblings by their set order and then by code
 * (Cesar, Nov 1: phases in E, P, C, CM order rather than alphabetical). Reports sort by this position.
 */
export function treeOrder<N extends { code: string; parentCode?: string; order?: number }>(list: N[]): N[] {
  const sib = (x: N, y: N) => (x.order ?? Infinity) - (y.order ?? Infinity) || x.code.localeCompare(y.code, undefined, { numeric: true });
  const codes = new Set(list.map((n) => n.code));
  const kids = new Map<string, N[]>();
  for (const n of list) {
    const p = n.parentCode && codes.has(n.parentCode) && n.parentCode !== n.code ? n.parentCode : "";
    kids.set(p, [...(kids.get(p) ?? []), n]);
  }
  const out: N[] = [];
  const seen = new Set<string>();
  const walk = (p: string) => {
    for (const n of (kids.get(p) ?? []).sort(sib)) {
      if (seen.has(n.code)) continue;
      seen.add(n.code);
      out.push(n);
      walk(n.code);
    }
  };
  walk("");
  for (const n of [...list].sort(sib)) if (!seen.has(n.code)) out.push(n); // loops, if any slipped in
  return out;
}
